import type {
  CreateGiftCardCheckoutRequest,
  GiftCardRedeemResponse,
  GiftCardPromotionPreview,
} from '@yoyoclub/shared';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

async function parseError(response: Response, fallback: string): Promise<never> {
  const data = await response.json().catch(() => ({}));
  throw new Error(data.error?.message || data.error || fallback);
}

export const giftCardApi = {
  async createCheckout(
    data: CreateGiftCardCheckoutRequest,
    token?: string
  ): Promise<{ checkoutUrl: string }> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const response = await fetch(`${API_URL}/gift-cards/create-checkout-session`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) await parseError(response, 'Failed to create checkout session');
    return (await response.json()).data;
  },

  async preview(input: { token: string }): Promise<{ faceValue: number; status: string }> {
    const response = await fetch(`${API_URL}/gift-cards/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!response.ok) await parseError(response, 'Failed to load gift card');
    return (await response.json()).data;
  },

  async redeem(
    input: { token?: string; code?: string },
    token: string
  ): Promise<GiftCardRedeemResponse> {
    const response = await fetch(`${API_URL}/gift-cards/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(input),
    });
    if (!response.ok) await parseError(response, 'Failed to redeem gift card');
    return (await response.json()).data;
  },

  /** No code -> active site-wide promo (null if none). With code -> that promo code's
   *  offers; an invalid code throws so the UI can show the reason. */
  async getActivePromotion(code?: string): Promise<GiftCardPromotionPreview | null> {
    const suffix = code ? `?code=${encodeURIComponent(code)}` : '';
    const response = await fetch(`${API_URL}/gift-cards/active-promotion${suffix}`);
    if (!response.ok) {
      if (code) await parseError(response, 'Invalid promo code');
      return null;
    }
    return (await response.json()).data;
  },

  // --- My Gift Cards (purchaser view) ---
  async listMyCards(token: string): Promise<MyGiftCardRow[]> {
    const response = await fetch(`${API_URL}/gift-cards/my`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to load your gift cards');
    return (await response.json()).data;
  },
  async resendOwnCard(id: string, token: string): Promise<void> {
    // Backend silently rate-limits; we always treat any 2xx as "resent" for UX.
    const response = await fetch(`${API_URL}/gift-cards/${id}/resend`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to resend');
  },

  // --- Admin ---
  async adminListCards(
    token: string,
    params: { search?: string; status?: string; cursor?: string } = {}
  ): Promise<{ items: GiftCardAdminRow[]; nextCursor: string | null }> {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.cursor) qs.set('cursor', params.cursor);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    const response = await fetch(`${API_URL}/admin/gift-cards${suffix}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to load gift cards');
    return (await response.json()).data;
  },
  async adminGetCard(id: string, token: string): Promise<GiftCardAdminDetail> {
    const response = await fetch(`${API_URL}/admin/gift-cards/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to load gift card');
    return (await response.json()).data;
  },
  async adminVoidCard(id: string, token: string): Promise<void> {
    const response = await fetch(`${API_URL}/admin/gift-cards/${id}/void`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to void');
  },
  async adminResendCard(id: string, token: string): Promise<void> {
    const response = await fetch(`${API_URL}/admin/gift-cards/${id}/resend`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to resend');
  },
  async adminListPromotions(token: string): Promise<GiftCardPromotionRow[]> {
    const response = await fetch(`${API_URL}/admin/gift-card-promotions`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to load promotions');
    return (await response.json()).data;
  },
  async adminCreatePromotion(
    data: Record<string, unknown>,
    token: string
  ): Promise<GiftCardPromotionRow> {
    const response = await fetch(`${API_URL}/admin/gift-card-promotions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!response.ok) await parseError(response, 'Failed to create promotion');
    return (await response.json()).data;
  },
  async adminGetPromotion(id: string, token: string): Promise<GiftCardPromotionRow> {
    const response = await fetch(`${API_URL}/admin/gift-card-promotions/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) await parseError(response, 'Failed to load promotion');
    return (await response.json()).data;
  },
  async adminUpdatePromotion(
    id: string,
    data: Record<string, unknown>,
    token: string
  ): Promise<GiftCardPromotionRow> {
    const response = await fetch(`${API_URL}/admin/gift-card-promotions/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(data),
    });
    if (!response.ok) await parseError(response, 'Failed to update promotion');
    return (await response.json()).data;
  },
};

export interface MyGiftCardRow {
  id: string;
  code: string;
  faceValue: number;
  paidAmount: number;
  bonusAmount: number;
  isGift: boolean;
  recipientEmail: string | null;
  recipientName: string | null;
  createdAt: string;
}

export interface GiftCardAdminRow {
  id: string;
  code: string;
  faceValue: number;
  paidAmount: number;
  status: string;
  isGift: boolean;
  purchaserEmail: string;
  recipientEmail: string | null;
  createdAt: string;
}

export interface GiftCardAdminDetail extends GiftCardAdminRow {
  recipientName: string | null;
  redeemedAt: string | null;
  promotion?: { id: string; name: string; code: string | null } | null;
  redeemer?: { id: string; email: string; name: string | null } | null;
  creditTransactions?: { id: string; type: string; amount: number; createdAt: string }[];
}

export interface GiftCardPromotionTierRow {
  id: string;
  payAmount: number;
  faceValue: number;
  sortOrder: number;
}

export interface GiftCardPromotionRow {
  id: string;
  name: string;
  description: string | null;
  triggerType: 'SITE_WIDE' | 'PROMO_CODE';
  code: string | null;
  tiers: GiftCardPromotionTierRow[];
  perEmailLimit: number | null;
  usageLimit: number | null;
  usedCount: number;
  validFrom: string | null;
  validUntil: string | null;
  isActive: boolean;
}
